// Combines the boards people publish into one, so that every page that reads the same boards ends
// with the same board, whatever order they arrive in.
//
// Each person publishes the whole board they hold; a reader merges each into its own, key by key:
// the change with the later `at` (ms since the epoch, by its writer's clock) wins, and of two with
// the same `at`, the one whose JSON text sorts last.

/** A key's last change: when it was made. */
export interface Change {
  at: number;
}

const newer = (a: Change, b: Change) => (a.at !== b.at ? a.at > b.at : JSON.stringify(a) > JSON.stringify(b));

/** Merges the board `from` (as published, unchecked) into `into`; true when `into` changed. */
export function merge<T extends Change>(into: Record<string, T>, from: unknown): boolean {
  if (typeof from !== 'object' || from === null || Array.isArray(from)) return false;
  let changed = false;
  for (const [key, change] of Object.entries(from as Record<string, unknown>)) {
    if (key === '__proto__' || typeof change !== 'object' || change === null || typeof (change as Change).at !== 'number') continue;
    const held = into[key];
    if (held && !newer(change as Change, held)) continue;
    into[key] = change as T;
    changed = true;
  }
  return changed;
}
